import { OrderStatus, PaymentStatus, ShippingStatus } from '../types';

/**
 * One line telling the customer where an order stands: what has left the warehouse, and whether an
 * invoice of ours is still waiting to be paid.
 *
 * `orders_status` cannot answer that. It is a single list holding two different questions - did it
 * leave the warehouse, and has it been settled - so which half it shows depends on who acted last,
 * and that order differs by payment method. A finished order reads "Order har lämnat lagret" when
 * the warehouse acted last and "Faktura skickad" when billing did, although both mean the same
 * thing. Those two statuses alone are 82% of every order ever placed. Reading `shipping_status`
 * instead gives the same answer for every payment method, whichever step came first.
 *
 * What is deliberately not said is that an order has been *paid*. `payment_status` 1000 means the
 * money reached us, which is not the same as the customer having parted with it: a third of orders
 * go through Kustom, where the status is set when Klarna settles with us at activation, and the
 * customer may still owe Klarna. `kco_payment_type` would tell the two apart, but it is empty on
 * 90% of those orders, so there is no way to know who has actually paid.
 *
 * `payment_status` 100 is a different thing and always true: our own invoice is out and unpaid.
 * That one is worth saying, because it is the only state that asks the customer for something.
 *
 * `delivery_status` would add where the parcel is, but it is uneven across vendors and markets, so
 * it is deliberately not part of this either.
 */

/* ShippingStatus */
const SHIPPED = 1000;
const PARTLY_SHIPPED = 500;
const AT_VENDOR = 100;

/* PaymentStatus */
const INVOICED = 100;
const PART_PAID = 500;

/**
 * Only ever read to stop saying something, never to claim payment: an advance invoice that has been
 * settled must not keep asking to be paid.
 */
const PAID = 1000;

/**
 * Cancelled and cancelled-by-customer, which no amount of shipping or invoicing describes. They
 * keep their own texts rather than sharing one: norwegian and danish say who did it - avbrutt
 * against avbestilt, annulleret against afbestilt - where swedish, english, dutch and german do not.
 */
const CANCELLED: number[] = [18, 19];

/** the goods went back, so what shipped is no longer the thing to say */
const RETURNED = 10;

/** the customer has to pay before we ship, which is the one status that asks something of them */
const PREPAY_INVOICE_SENT = 7;

interface OrderStatusTexts {
	orderSummaryStatus: {
		shipped: string;
		shippedAndInvoiced: string;
		partlyShipped: string;
		partlyShippedAndInvoiced: string;
		invoiced: string;
	};
	visibleOrderStatus: Record<string, string>;
}

export interface SummarisableOrder {
	status: { id: OrderStatus };
	delivery?: { status?: ShippingStatus };
	payment?: { status?: PaymentStatus };
}

export const orderStatusSummary = (order: SummarisableOrder, t: OrderStatusTexts): string => {
	if (CANCELLED.includes(order.status.id)) return t.visibleOrderStatus[String(order.status.id)];
	if (order.status.id === RETURNED) return t.visibleOrderStatus['10'];

	const shipping = order.delivery?.status;
	const payment = order.payment?.status;

	// an older api, or a market not yet on this release: fall back to what the order status says
	if (shipping === undefined || payment === undefined) {
		return t.visibleOrderStatus[String(order.status.id)] ?? '';
	}

	// part paid means an invoice is out and some of it is settled, which "invoiced" covers
	const invoiced = payment === INVOICED || payment === PART_PAID;

	if (shipping === SHIPPED) {
		return invoiced ? t.orderSummaryStatus.shippedAndInvoiced : t.orderSummaryStatus.shipped;
	}

	if (shipping === PARTLY_SHIPPED) {
		return invoiced ? t.orderSummaryStatus.partlyShippedAndInvoiced : t.orderSummaryStatus.partlyShipped;
	}

	// an unpaid advance invoice is why the order is standing still, and saying "behandlas" hides it
	if (order.status.id === PREPAY_INVOICE_SENT && payment !== PAID) return t.visibleOrderStatus['7'];

	/*
	 * An invoice asks the same of the customer whether or not the goods have gone yet. It needs its
	 * own text rather than status 5's: norwegian and danish spell that one "Ordre sendt - faktura
	 * sendt", which would claim a shipment that has not happened.
	 */
	if (invoiced) return t.orderSummaryStatus.invoiced;

	return shipping === AT_VENDOR ? t.visibleOrderStatus['4'] : t.visibleOrderStatus['1'];
};
